import React, {useEffect, useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Container, Typography} from '@mui/material';
import {Add as AddIcon, ExpandMore as ExpandMoreIcon, Person as PersonIcon} from '@mui/icons-material';
import {useAuth} from '../../context/AuthContext';
import memberService from '../../services/memberService';
import unitService from '../../services/unitService';
import MemberForm from './MemberForm';
import MemberTable from './MemberTable';
import DeleteMemberDialog from './DeleteMemberDialog';

const ManageMembers = () => {
    const {user, isYouthLeader} = useAuth();
    const [members, setMembers] = useState([]);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Dialog States
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [deletingMember, setDeletingMember] = useState(null);
    const [viewMode, setViewMode] = useState(false);

    // Berechtigungen
    const canEdit = isYouthLeader();
    const canDelete = isYouthLeader();
    const canCreate = isYouthLeader();

    // Form Data
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        cityOfBirth: '',
        gender: '',
        memberCardId: '',
        activeSince: '',
        activeUntil: '',
        isActive: true, // Neues Boolean-Feld
        unitId: '',
        // Adresse
        street: '',
        houseNumber: '',
        city: '',
        zipCode: '',
        addressId: '',
    });

    useEffect(() => {
        loadMembers();
        loadUnits();
    }, []);

    // Filtere aktive und inaktive Mitglieder
    const activeMembers = members.filter(member => !member.activeUntil);
    const inactiveMembers = members.filter(member => member.activeUntil);

    const loadMembers = async () => {
        try {
            setLoading(true);
            const data = await memberService.getAllMembers();
            setMembers(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Fehler beim Laden der Mitglieder:', err);
            setError('Fehler beim Laden der Mitglieder: ' + (err.response?.data?.message || err.message));
            setMembers([]);
        } finally {
            setLoading(false);
        }
    };

    const loadUnits = async () => {
        try {
            const data = await unitService.getAllUnits();
            setUnits(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Fehler beim Laden der Units:', err);
            setUnits([]);
        }
    };

    const handleOpenDialog = (member = null) => {
        setViewMode(false); // Bearbeitungsmodus
        if (member) {
            setEditingMember(member);
            setFormData({
                firstName: member.firstName,
                lastName: member.lastName,
                dateOfBirth: member.dateOfBirth ? formatDateForInput(member.dateOfBirth) : '',
                cityOfBirth: member.cityOfBirth || '',
                gender: member.gender || '',
                memberCardId: member.memberCardId || '',
                activeSince: member.activeSince ? formatDateForInput(member.activeSince) : '',
                activeUntil: member.activeUntil ? formatDateForInput(member.activeUntil) : '',
                isActive: !member.activeUntil, // Aktiv wenn kein activeUntil gesetzt ist
                unitId: member.unit?.id || '',
                // Adresse
                street: member.address?.street || '',
                houseNumber: member.address?.houseNumber || '',
                city: member.address?.city || '',
                zipCode: member.address?.zipCode || '',
                addressId: member.address?.id || '',
            });
        } else {
            setEditingMember(null);
            setFormData({
                firstName: '',
                lastName: '',
                dateOfBirth: '',
                cityOfBirth: '',
                gender: '',
                memberCardId: '',
                activeSince: new Date().toISOString().split('T')[0],
                activeUntil: '',
                isActive: true, // Standardmäßig aktiv
                unitId: '', // Wird vom Backend automatisch gesetzt
                // Adresse
                street: '',
                houseNumber: '',
                city: '',
                zipCode: '',
                addressId: '',
            });
        }
        setOpenDialog(true);
    };

    const handleViewDialog = (member) => {
        setViewMode(true); // Ansichtsmodus
        setEditingMember(member);
        setFormData({
            firstName: member.firstName,
            lastName: member.lastName,
            dateOfBirth: member.dateOfBirth ? formatDateForInput(member.dateOfBirth) : '',
            cityOfBirth: member.cityOfBirth || '',
            gender: member.gender || '',
            memberCardId: member.memberCardId || '',
            activeSince: member.activeSince ? formatDateForInput(member.activeSince) : '',
            activeUntil: member.activeUntil ? formatDateForInput(member.activeUntil) : '',
            isActive: !member.activeUntil,
            unitId: member.unit?.id || '',
            // Adresse
            street: member.address?.street || '',
            houseNumber: member.address?.houseNumber || '',
            city: member.address?.city || '',
            zipCode: member.address?.zipCode || '',
            addressId: member.address?.id || '',
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingMember(null);
        setViewMode(false);
        setFormData({
            firstName: '',
            lastName: '',
            dateOfBirth: '',
            cityOfBirth: '',
            gender: '',
            memberCardId: '',
            activeSince: '',
            activeUntil: '',
            isActive: true,
            unitId: '',
            // Adresse
            street: '',
            houseNumber: '',
            city: '',
            zipCode: '',
            addressId: '',
        });
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toISOString().split('T')[0];
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            const submitData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth).toISOString() : null,
                cityOfBirth: formData.cityOfBirth || null,
                gender: formData.gender || null,
                memberCardId: formData.memberCardId || null,
                activeSince: formData.activeSince ? new Date(formData.activeSince).toISOString() : null,
                // activeUntil: Behandle leeren String explizit als null
                activeUntil: (formData.activeUntil && formData.activeUntil.trim() !== '')
                    ? new Date(formData.activeUntil).toISOString()
                    : null,
                // Adresse
                street: formData.street || null,
                houseNumber: formData.houseNumber || null,
                city: formData.city || null,
                zipCode: formData.zipCode || null,
            };

            // UnitId: beim Bearbeiten senden, beim Erstellen nicht (Backend setzt automatisch)
            if (editingMember && formData.unitId) {
                submitData.unitId = parseInt(formData.unitId);
            }

            // AddressId nur beim Bearbeiten wenn vorhanden
            if (editingMember && formData.addressId) {
                submitData.addressId = parseInt(formData.addressId);
            }

            if (editingMember) {
                await memberService.updateMember(editingMember.id, submitData);
                setSuccess('Mitglied erfolgreich aktualisiert!');
            } else {
                await memberService.createMember(submitData);
                setSuccess('Mitglied erfolgreich erstellt!');
            }
            handleCloseDialog();
            loadMembers();
        } catch (err) {
            setError(err.response?.data?.message || 'Fehler beim Speichern des Mitglieds');
        }
    };

    const handleOpenDeleteDialog = (member) => {
        setDeletingMember(member);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setDeletingMember(null);
    };

    const handleDelete = async () => {
        try {
            await memberService.deleteMember(deletingMember.id);
            setSuccess('Mitglied erfolgreich gelöscht!');
            handleCloseDeleteDialog();
            loadMembers();
        } catch (err) {
            setError('Fehler beim Löschen des Mitglieds: ' + (err.response?.data?.message || err.message));
        }
    };


    if (loading) {
        return (
            <Container maxWidth="lg" sx={{mt: 4}}>
                <Typography>Lade Mitglieder...</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{mt: 4, mb: 4}}>
            <Box sx={{mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                    <PersonIcon sx={{fontSize: 40, color: 'primary.main'}}/>
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            Mitglieder
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Verwalten Sie die Mitglieder Ihrer Jugendfeuerwehr
                        </Typography>
                    </Box>
                </Box>
                {canCreate && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon/>}
                        onClick={() => handleOpenDialog()}
                    >
                        Neues Mitglied
                    </Button>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{mb: 2}} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{mb: 2}} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {/* Aktive Mitglieder */}
            <Box sx={{mb: 3}}>
                <Typography variant="h6" sx={{mb: 2}}>
                    Aktive Mitglieder ({activeMembers.length})
                </Typography>
                <MemberTable
                    members={activeMembers}
                    onEdit={handleOpenDialog}
                    onDelete={handleOpenDeleteDialog}
                    onView={handleViewDialog}
                    canEdit={canEdit}
                    canDelete={canDelete}
                />
            </Box>

            {/* Inaktive Mitglieder in Accordion */}
            {inactiveMembers.length > 0 && (
                <Accordion sx={{mb: 3}}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon/>}
                        aria-controls="inactive-members-content"
                        id="inactive-members-header"
                    >
                        <Typography variant="h6">
                            Inaktive Mitglieder ({inactiveMembers.length})
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <MemberTable
                            members={inactiveMembers}
                            onEdit={handleOpenDialog}
                            onDelete={handleOpenDeleteDialog}
                            onView={handleViewDialog}
                            canEdit={canEdit}
                            canDelete={canDelete}
                        />
                    </AccordionDetails>
                </Accordion>
            )}

            <MemberForm
                open={openDialog}
                editingMember={editingMember}
                formData={formData}
                units={units}
                user={user}
                onClose={handleCloseDialog}
                onSubmit={handleSubmit}
                onFormDataChange={setFormData}
                viewMode={viewMode}
            />

            <DeleteMemberDialog
                open={openDeleteDialog}
                member={deletingMember}
                onClose={handleCloseDeleteDialog}
                onConfirm={handleDelete}
            />
        </Container>
    );
};

export default ManageMembers;

